import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ScholarshipsService } from '../../../services/scholarships/scholarships.service';
import { GetScholarshipRequest, ScholarshipType } from '../../../models/scholarships/getScholarshipRequest';
import { ScholarshipResponse } from '../../../models/scholarships/scholarshipResponse';
import { MessageService } from 'primeng/api';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { DropdownModule } from 'primeng/dropdown';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { MentorService } from '../../../services/account/mentor.service';
import { UtilityService } from '../../../services/utility/utility.service';
import { MentorType } from '../../../models/enums/enums';
import { forkJoin } from 'rxjs';
import { formatDeadline, parseDeadline } from '../../../pipe/deadlinePipe'; 

@Component({
  selector: 'app-deadline-calendar',
  standalone: true,
  imports: [CommonModule, ProgressSpinnerModule, DropdownModule, FormsModule, DialogModule, OverlayPanelModule],
  templateUrl: './deadline-calendar.component.html',
  styleUrls: ['./deadline-calendar.component.scss']
})
export class DeadlineCalendarComponent implements OnInit {

  months = ['JANUARY', 'FEBRUARY', 'MARCH', 'APRIL', 'MAY', 'JUNE', 'JULY', 'AUGUST', 'SEPTEMBER', 'OCTOBER', 'NOVEMBER', 'DECEMBER'];
  selectedMonth = 'JANUARY';
  isLoading = false;

  timelinePoints: { month: string, count: number, color: string }[] = [];
  next30Days: any[] = [];
  allScholarshipsList: ScholarshipResponse[] = [];
  myScholarshipsList: ScholarshipResponse[] = [];

  // Dialog for scholarship details
  scholarshipDialogVisible = false;
  selectedScholarship: ScholarshipResponse | null = null;
  loadingScholarshipId?: number | null;

  // Mentor Sharing
  studentMentors: any[] = [];
  selectedMentorToShare: any = null;
  isSharing = false;

  countryCodeMap: { [key: string]: string } = {};

  // Sort options
  sortOptions = [
    { label: 'Relevance', value: 'relevance' },
    { label: 'Deadline (Soonest)', value: 'deadline-asc' },
    { label: 'Deadline (Latest)', value: 'deadline-desc' },
    { label: 'Name (A-Z)', value: 'name-asc' },
    { label: 'Value (High-Low)', value: 'value-desc' }
  ];
  selectedSort = 'relevance';

  constructor(
    private scholarshipsService: ScholarshipsService,
    private messageService: MessageService,
    private router: Router,
    private mentorService: MentorService,
    private utilityService: UtilityService
  ) { }

  goToCards(): void {
    this.router.navigate(['/portal/scholarships']);
  }

  goToMap(): void {
    this.router.navigate(['/portal/scholarships-map']);
  }

  goToMyScholarships(): void {
    // Navigate back to cards but with my scholarships filter potentially? 
    // For now standard navigation as per map view
    this.router.navigate(['/portal/scholarships']);
  }

  ngOnInit(): void {
    const currentMonthIndex = new Date().getMonth();
    this.selectedMonth = this.months[currentMonthIndex];
    this.initTimelinePoints();
    this.initializeData();
  }

  initTimelinePoints() {
    this.timelinePoints = this.months.map((m) => {
      return { month: m, count: 0, color: '#e2e8f0' };
    });
  }

  initializeData(): void {
    this.isLoading = true;
    const req: GetScholarshipRequest = {
      scholarshipName: '',
      scholarshipType: ScholarshipType.Scholarship,
      stream: null,
      level: null,
    };

    forkJoin({
      scholarships: this.scholarshipsService.getScholarships(req),
      myScholarships: this.scholarshipsService.getMyScholarships(),
      countries: this.utilityService.getCountries(),
      mentors: this.mentorService.getStudentMentors(MentorType.Mentor)
    }).subscribe({
      next: ({ scholarships, myScholarships, countries, mentors }) => {
        // 1. Process Country Code Map
        if (countries && countries.result) {
          countries.result.forEach(c => {
            this.countryCodeMap[c.name.toLowerCase()] = c.countryCode.toLowerCase();
          });
        }

        // 2. Process Mentors
        if (mentors && mentors.result) {
          this.studentMentors = mentors.result.map(m => ({
            label: `${m.firstName} ${m.lastName}`,
            value: m.mentorId
          }));
        }

        // 3. Process Bookmarks
        const myScholarshipsIds = myScholarships ? myScholarships.result.map(s => s.scholarship.id) : [];
        this.myScholarshipsList = myScholarships ? myScholarships.result.map(item => ({
          ...item.scholarship,
          isMyScholarship: true
        })) : [];

        // 4. Process all scholarships
        this.allScholarshipsList = scholarships && scholarships.result ? scholarships.result.map(item => ({
          ...item,
          isMyScholarship: myScholarshipsIds.includes(item.id)
        })) : [];

        this.processDeadlines();
        this.isLoading = false;
      },
      error: (err) => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Error', detail: `Error Fetching Data: ${err}` });
      }
    });
  }

  processDeadlines() {
    // Reset counts
    this.initTimelinePoints();
    const next30List: any[] = [];
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const in30Days = new Date(today);
    in30Days.setDate(today.getDate() + 30);

    // Map month names to index
    const monthMap: { [key: string]: number } = {
      'january': 0, 'february': 1, 'march': 2, 'april': 3, 'may': 4, 'june': 5,
      'july': 6, 'august': 7, 'september': 8, 'october': 9, 'november': 10, 'december': 11,
      'jan': 0, 'feb': 1, 'mar': 2, 'apr': 3, 'jun': 5, 'jul': 6, 'aug': 7, 'sep': 8, 'oct': 9, 'nov': 10, 'dec': 11
    };

    this.allScholarshipsList.forEach(s => {
      if (!s.deadLine) return;

      const deadline = s.deadLine;
      let monthStr = '';
      let exactDateStr: string | null = null;

      if (typeof deadline === 'object' && deadline !== null) {
        monthStr = (deadline.month || '').toLowerCase();
        exactDateStr = deadline.exactDate || null;
      } else {
        monthStr = String(deadline).toLowerCase();
        exactDateStr = String(deadline);
      }

      if (!monthStr) return;

      // Find the month index
      let sMonthIndex = -1;
      for (const key in monthMap) {
        if (monthStr.includes(key)) {
          sMonthIndex = monthMap[key];
          break;
        }
      }

      if (sMonthIndex !== -1) {
        // Increment timeline count
        this.timelinePoints[sMonthIndex].count++;

        // Calculate if it's within next 30 days
        const dayMatch = (exactDateStr || monthStr).match(/\d+/);
        const day = dayMatch ? parseInt(dayMatch[0], 10) : null;
        
        let sYear = today.getFullYear();
        // If the month index is less than current month, it's definitely next year
        if (sMonthIndex < today.getMonth()) {
          sYear++;
        }

        let shouldInclude = false;
        let displayDate = "";
        let sortDate: Date;

        if (day !== null) {
          sortDate = new Date(sYear, sMonthIndex, day);
          if (sortDate >= today && sortDate <= in30Days) {
            shouldInclude = true;
            displayDate = exactDateStr || `${this.months[sMonthIndex]} ${day}`;
          }
        } else {
          // No exact date - check if it's the current month or the next month in our 30-day window
          const currentMonth = today.getMonth();
          const nextMonth = in30Days.getMonth();
          
          if (sMonthIndex === currentMonth || sMonthIndex === nextMonth) {
            shouldInclude = true;
            displayDate = this.months[sMonthIndex];
            // For sorting, put month-only deadlines at the end of their month
            sortDate = new Date(sYear, sMonthIndex, 28); 
          } else {
            sortDate = new Date(sYear, sMonthIndex, 1);
          }
        }

        if (shouldInclude) {
          const country = s.country;
          let flagCode = null;
          if (country) {
            const firstCountry = Array.isArray(country) ? country[0] : country;
            if (typeof firstCountry === 'string') {
              if (firstCountry.length === 2) {
                flagCode = firstCountry.toLowerCase();
              } else {
                const code = this.countryCodeMap[firstCountry.toLowerCase()];
                if (code) {
                  flagCode = code;
                }
              }
            }
          }

          next30List.push({
            dateObj: sortDate,
            date: displayDate,
            name: s.name,
            flag: flagCode,
            icon: !flagCode ? 'pi-briefcase' : null,
            color: day !== null ? '#e55b5b' : '#f59e0b', // Red for exact, Orange for month-only
            hasExactDate: day !== null,
            scholarship: s
          });
        }
      }
    });

    // Update colors based on counts
    this.timelinePoints.forEach(p => {
      if (p.count > 10) p.color = '#60a5fa'; // Blue
      else if (p.count > 5) p.color = '#f59e0b'; // Orange
      else if (p.count > 0) p.color = '#fbbf24'; // Yellow
      else p.color = '#e2e8f0'; // Gray
    });

    // Sort next30Days: Exact dates first, then month-only, then by date
    next30List.sort((a, b) => {
      if (a.hasExactDate && !b.hasExactDate) return -1;
      if (!a.hasExactDate && b.hasExactDate) return 1;
      return a.dateObj.getTime() - b.dateObj.getTime();
    });
    this.next30Days = next30List;
  }

  onBubbleClick(monthShort: string): void {
    const fullMonths = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const monthIndex = this.months.indexOf(monthShort);
    if (monthIndex !== -1) {
      const monthFull = fullMonths[monthIndex];
      this.router.navigate(['/portal/scholarships'], { queryParams: { month: monthFull } });
    }
  }

  // Open scholarship details dialog
  openScholarshipDetails(scholarship: ScholarshipResponse): void {
    this.selectedScholarship = scholarship;
    this.scholarshipDialogVisible = true;
  }

  // Close scholarship details dialog
  closeScholarshipDialog(): void {
    this.scholarshipDialogVisible = false;
    this.selectedScholarship = null;
  }

  toggleMyScholarship(id: number) {
    const index = this.allScholarshipsList.findIndex(s => s.id === id);
    if (index === -1) return;

    const current = this.allScholarshipsList[index];
    const newValue = !current.isMyScholarship;

    // Optimistically update
    this.allScholarshipsList = this.allScholarshipsList.map(item =>
      item.id === id ? { ...item, isMyScholarship: newValue } : item
    );
    if (this.selectedScholarship && this.selectedScholarship.id === id) {
      this.selectedScholarship = { ...this.selectedScholarship, isMyScholarship: newValue };
    }

    // Also update references in next30Days
    this.next30Days = this.next30Days.map(item => {
      if (item.scholarship && item.scholarship.id === id) {
        return {
          ...item,
          scholarship: { ...item.scholarship, isMyScholarship: newValue }
        };
      }
      return item;
    });

    this.loadingScholarshipId = id;

    if (newValue) {
      this.scholarshipsService.addMyScholarship(id).subscribe({
        next: () => {
          this.loadingScholarshipId = null;
          const savedItem = this.allScholarshipsList.find(item => item.id === id);
          if (savedItem && !this.myScholarshipsList.some(item => item.id === id)) {
            this.myScholarshipsList.push({ ...savedItem, isMyScholarship: true });
          }
        },
        error: (err) => {
          console.warn('Error adding scholarship:', err);
          // Rollback
          this.allScholarshipsList = this.allScholarshipsList.map(item =>
            item.id === id ? { ...item, isMyScholarship: false } : item
          );
          if (this.selectedScholarship && this.selectedScholarship.id === id) {
            this.selectedScholarship = { ...this.selectedScholarship, isMyScholarship: false };
          }
          this.next30Days = this.next30Days.map(item => {
            if (item.scholarship && item.scholarship.id === id) {
              return {
                ...item,
                scholarship: { ...item.scholarship, isMyScholarship: false }
              };
            }
            return item;
          });
          this.myScholarshipsList = this.myScholarshipsList.filter(item => item.id !== id);
          this.loadingScholarshipId = null;
        }
      });
    } else {
      this.scholarshipsService.deleteMyScholarShip(id).subscribe({
        next: () => {
          this.loadingScholarshipId = null;
          this.myScholarshipsList = this.myScholarshipsList.filter(item => item.id !== id);
        },
        error: (err) => {
          console.warn('Error removing scholarship:', err);
          // Rollback
          this.allScholarshipsList = this.allScholarshipsList.map(item =>
            item.id === id ? { ...item, isMyScholarship: true } : item
          );
          if (this.selectedScholarship && this.selectedScholarship.id === id) {
            this.selectedScholarship = { ...this.selectedScholarship, isMyScholarship: true };
          }
          this.next30Days = this.next30Days.map(item => {
            if (item.scholarship && item.scholarship.id === id) {
              return {
                ...item,
                scholarship: { ...item.scholarship, isMyScholarship: true }
              };
            }
            return item;
          });
          const savedItem = this.allScholarshipsList.find(item => item.id === id);
          if (savedItem && !this.myScholarshipsList.some(item => item.id === id)) {
            this.myScholarshipsList.push({ ...savedItem, isMyScholarship: true });
          }
          this.loadingScholarshipId = null;
        }
      });
    }
  }

  shareScholarshipWithMentor() {
    if (!this.selectedMentorToShare || !this.selectedScholarship) return;

    this.isSharing = true;
    setTimeout(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Shared',
        detail: `Scholarship shared with ${this.selectedMentorToShare.label}`
      });
      this.isSharing = false;
      this.selectedMentorToShare = null;
    }, 1000);
  }

  navigateToMentors() {
    this.closeScholarshipDialog();
    this.router.navigate(['/portal/mentor-me']);
  }

  getFormattedAmount(item: any): string {
    if (!item) return 'Varies';

    // 1. Prioritize USD (currency === 0) from the amount array
    let amtStr = '';
    const amounts = item.scholarship?.amount || [];
    const usdAmountObj = amounts.find((a: any) => a.currency === 0);
    
    if (usdAmountObj && usdAmountObj.amount) {
      amtStr = usdAmountObj.amount.toString().trim();
    } else if (amounts.length > 0 && amounts[0].amount) {
      amtStr = amounts[0].amount.toString().trim();
    } else if (item.scholarshipAmount) {
      amtStr = item.scholarshipAmount.toString().trim();
    } else if (item.scholarship?.exactNumbersUnavailable) {
      amtStr = item.scholarship.exactNumbersUnavailable.toString().trim();
    }

    if (!amtStr) {
      if (item.fullFunding) return 'Full Funding';
      return 'Varies';
    }

    const lowerAmt = amtStr.toLowerCase();
    if (lowerAmt === 'varies' || lowerAmt === 'variable' || lowerAmt === 'varies/variable' || lowerAmt === 'varies / variable') {
      if (item.fullFunding) return 'Full Funding';
      return 'Varies';
    }

    // Strip currency symbols and commas to check if it's purely numeric
    const numericClean = amtStr.replace(/[$\u00A3\u20AC\u20B9\u20A8,\s]/g, '');
    if (/^\d+(\.\d+)?$/.test(numericClean)) {
      const num = parseFloat(numericClean);
      // Prepend original currency symbol if it was present
      if (amtStr.includes('£') || amtStr.includes('\u00A3')) {
        return '£' + num.toLocaleString('en-US', { maximumFractionDigits: 0 });
      }
      if (amtStr.includes('€') || amtStr.includes('\u20AC')) {
        return '€' + num.toLocaleString('en-US', { maximumFractionDigits: 0 });
      }
      if (amtStr.includes('₹') || amtStr.includes('\u20B9')) {
        return '₹' + num.toLocaleString('en-US', { maximumFractionDigits: 0 });
      }
      return '$' + num.toLocaleString('en-US', { maximumFractionDigits: 0 });
    }

    if (/^[\$\£\€\¥\₹]/.test(amtStr)) {
      return amtStr;
    }

    return amtStr;
  }

  getScholarshipImage(item: any): string | null {
    if (!item) return null;
    if (item.image && typeof item.image === 'string') {
      const img = item.image.trim();
      const isUrl = img.startsWith('http://') || img.startsWith('https://');
      if (isUrl) {
        return img;
      }
    }
    return null;
  }

  getCountryFlag(country: string | string[] | null | undefined): string | null {
    if (!country) return null;
    const name = Array.isArray(country) ? country[0] : country;
    if (!name) return null;

    if (name.toLowerCase() === 'global') {
      return null;
    }

    const code = this.countryCodeMap[name.toLowerCase()];
    if (code) {
      return `https://flagcdn.com/w40/${code}.png`;
    }

    const common: { [key: string]: string } = {
      'usa': 'us', 'uk': 'gb', 'india': 'in', 'canada': 'ca', 'australia': 'au',
      'germany': 'de', 'france': 'fr', 'china': 'cn', 'japan': 'jp'
    };
    const fallbackCode = common[name.toLowerCase()];
    return fallbackCode ? `https://flagcdn.com/w40/${fallbackCode}.png` : null;
  }

  formatMonth(month: string | null | undefined): string {
    if (!month) return 'Varies';
    const m = month.toLowerCase().trim();
    const map: { [key: string]: string } = {
      'jan': 'January', 'january': 'January',
      'feb': 'February', 'february': 'February',
      'mar': 'March', 'march': 'March',
      'apr': 'April', 'april': 'April',
      'may': 'May',
      'jun': 'June', 'june': 'June',
      'jul': 'July', 'july': 'July',
      'aug': 'August', 'august': 'August',
      'sep': 'September', 'september': 'September',
      'oct': 'October', 'october': 'October',
      'nov': 'November', 'november': 'November',
      'dec': 'December', 'december': 'December'
    };
    return map[m] || (month.charAt(0).toUpperCase() + month.slice(1).toLowerCase());
  }

  getAcademicMainText(reqs: any): string {
    if (!reqs || typeof reqs !== 'string') return "Bachelor's degree";
    const clean = reqs.trim();
    if (clean.includes('\n')) return clean.split('\n')[0].trim();
    if (clean.includes(';')) return clean.split(';')[0].trim();
    return clean;
  }

  getAcademicSubText(reqs: any): string | null {
    if (!reqs || typeof reqs !== 'string') {
      if (this.selectedScholarship?.id === 1 || (this.selectedScholarship?.name && this.selectedScholarship.name.includes('Fulbright'))) {
        return "3+ years work experience";
      }
      return null;
    }
    const clean = reqs.trim();
    if (clean.includes('\n')) return clean.split('\n')[1].trim();
    if (clean.includes(';')) return clean.split(';')[1].trim();
    if (this.selectedScholarship?.id === 1 || (this.selectedScholarship?.name && this.selectedScholarship.name.includes('Fulbright'))) {
      return "3+ years work experience";
    }
    return null;
  }

  getCountryDisplayName(country: any): string {
    if (!country) return 'Global';
    let name: any = Array.isArray(country) ? country[0] : country;
    if (!name) return 'Global';
    if (typeof name !== 'string') {
      name = name.name || name.toString() || '';
    }
    const lower = name.toLowerCase().trim();
    if (lower === 'united states' || lower === 'united states of america' || lower === 'us') {
      return 'USA';
    }
    if (lower === 'united kingdom' || lower === 'uk') {
      return 'UK';
    }
    return name;
  }

  getLevelDisplayName(level: any): string {
    if (!level || typeof level !== 'string') return 'Any';
    const lvl = level.toLowerCase().trim();
    if (lvl === 'post graduate' || lvl === 'pg' || lvl === 'postgraduate') return 'PG (Postgraduate)';
    if (lvl === 'under graduate' || lvl === 'ug' || lvl === 'undergraduate') return 'UG (Undergraduate)';
    if (lvl === 'phd' || lvl === 'doctorate') return 'PhD (Doctorate)';
    return level;
  }

  isCovered(type: 'tuition' | 'living' | 'travel' | 'health'): boolean {
    if (!this.selectedScholarship) return false;
    if (this.selectedScholarship.fullFunding) return true;
    
    let funding: any[] = [];
    if (Array.isArray(this.selectedScholarship.fundingType)) {
      funding = this.selectedScholarship.fundingType;
    } else if (typeof this.selectedScholarship.fundingType === 'string') {
      funding = [this.selectedScholarship.fundingType];
    }
    
    const fundingLower = funding
      .filter(f => typeof f === 'string')
      .map(f => f.toLowerCase().trim());
    
    if (type === 'tuition') {
      return fundingLower.some(f => f.includes('tuition') || f.includes('tution') || f.includes('full') || f.includes('stipend') || f.includes('fee'));
    }
    if (type === 'living') {
      return fundingLower.some(f => f.includes('living') || f.includes('full') || f.includes('stipend') || f.includes('accommodation'));
    }
    if (type === 'travel') {
      return fundingLower.some(f => f.includes('travel') || f.includes('full') || f.includes('airfare'));
    }
    if (type === 'health') {
      return fundingLower.some(f => f.includes('health') || f.includes('full') || f.includes('insurance') || f.includes('medical'));
    }
    return false;
  }

  getCoverageType(): string {
    if (!this.selectedScholarship) return '';
    if (this.selectedScholarship.fullFunding) return 'Full • 100%';
    
    let coveredCount = 0;
    if (this.isCovered('tuition')) coveredCount++;
    if (this.isCovered('living')) coveredCount++;
    if (this.isCovered('travel')) coveredCount++;
    if (this.isCovered('health')) coveredCount++;
    
    if (coveredCount === 0) return 'Partial';
    return `Partial • ${coveredCount * 25}%`;
  }

  getDeadlineDisplay(deadLine: any): string {
    if (!deadLine) return 'Varies';
    // GP-CB-05: when there is an exact deadline, show it in the fixed format ("March 31, 2026") instead of month + date.
    if (typeof deadLine !== 'string') {
      const exact = parseDeadline(deadLine.exactDate || deadLine.ExactDate || deadLine.exactdate);
      if (exact) return formatDeadline(exact);
    }

    let displayStr = '';
    if (typeof deadLine === 'string') {
      displayStr = deadLine;
    } else {
      const monthVal = deadLine.month || deadLine.Month;
      const dateVal = deadLine.exactDate || deadLine.ExactDate || deadLine.exactdate || '';
      
      if (!monthVal) return 'Varies';
      const month = this.formatMonth(monthVal);
      const date = dateVal ? ` ${dateVal}` : '';
      displayStr = `${month}${date}`;
    }
    
    let clean = displayStr.split('T')[0];
    clean = clean.replace(/\b\d{1,2}:\d{2}(?::\d{2})?\s*(?:AM|PM|am|pm)?/gi, '');
    clean = clean.replace(/\b(?:AM|PM|am|pm)\b/gi, '');
    clean = clean.replace(/\s+/g, ' ').trim();
    if (clean.endsWith(',')) {
      clean = clean.slice(0, -1).trim();
    }
    return clean;
  }

  getCountryPillName(country: any): string {
    if (!country) return 'Global';
    let name: any = Array.isArray(country) ? country[0] : country;
    if (!name) return 'Global';
    if (typeof name !== 'string') {
      name = name.name || name.toString() || 'Global';
    }
    const lower = name.toLowerCase().trim();
    if (lower === 'usa' || lower === 'us' || lower === 'united states' || lower === 'united states of america') {
      return 'United States';
    }
    if (lower === 'uk' || lower === 'united kingdom') {
      return 'United Kingdom';
    }
    return name;
  }

  getStreamDisplayName(stream: any): string {
    if (!stream) return 'Varies';
    if (Array.isArray(stream)) {
      return stream.join(', ');
    }
    if (typeof stream !== 'string') {
      return stream.toString() || 'Varies';
    }
    return stream;
  }

}

